<template><div><div class="d-m-400"></div>
<h2 id="overview" tabindex="-1" class="d-docsite--header-2">Overview <a class="header-anchor d-link d-docsite--link d-link" href="#overview" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Dialtone's color system offers a consistent visual foundation with a brand purple, versatile grayscale, and semantic status colors. Subtle gradients are used for promotional accents.</p>
<p class="d-docsite--paragraph">What we prioritize is semantic clarity, ensuring each color serves a distinct purpose within the UI for clear communication and consistency...</p>
<div class="d-m-400"></div>
<div class="d-bgc-secondary d-bar-400">
 <svg-loader name="color-table" />
</div>
<h2 id="use-semantic-color-tokens" tabindex="-1" class="d-docsite--header-2">Use Semantic Color Tokens <a class="header-anchor d-link d-docsite--link d-link" href="#use-semantic-color-tokens" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Dialtone uses <strong>semantic tokens</strong> for color, representing a color's <em>purpose</em> in the UI. See our <a href="https://dialtone.dialpad.com/tokens/" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">See our design token list</a> for descriptions guiding their application.</p>
<div class="d-bgc-secondary d-bar-400"><svg-loader class="d-fl1" name="color-tokens" /></div>
<div class="d-m-400"></div>
<p class="d-docsite--paragraph">It's crucial to understand that while these semantic tokens are built upon our underlying <strong>base color palette</strong>, the work of selecting the appropriate base color for each specific UI context has already been meticulously done for you.</p>
<div class="d-bgc-secondary d-bar-400"><svg-loader class="d-fl1" name="color-semantic" /></div>
<h2 id="color-roles" tabindex="-1" class="d-docsite--header-2">Color roles <a class="header-anchor d-link d-docsite--link d-link" href="#color-roles" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Color roles describe the purpose of how color is used. Each uses neutrals for general UI and status colors for impact.</p>
<div class="d-bgc-secondary d-bar-400"><svg-loader class="d-fl1" name="color-roles" /></div>
<p class="d-docsite--paragraph">We categorize color application by semantic roles such as: <strong>text, surfaces, borders, and themes</strong>. Each uses neutrals for general UI and status colors for impact.</p>
<h3 id="foreground-colors" tabindex="-1" class="d-docsite--header-3">Foreground Colors <a class="header-anchor d-link d-docsite--link d-link" href="#foreground-colors" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Foreground colors are the visual language of our content, applied across all content considered to be &quot;in the foreground&quot;, i.e. text and icons. You can utilize foreground tokens which can be found within the <a href="https://dialtone.dialpad.com/tokens/" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">tokens documentation here</a>.</p>
<div class="d-d-grid d-g-300 d-g-cols2">
  <div>
    <div class="d-bgc-secondary d-bar-400"><svg-loader class="d-fl1" name="color-text-f" /></div>
    <h4>Foreground Text</h4>
    Neutral text colors form the backbone of our content, used for headers, body text, forms, and more.
  </div>
  <div>
     <div class="d-bgc-secondary d-bar-400"><svg-loader class="d-fl1" name="color-text-s" /></div>
    <h4>Status Text</h4>
    Status text colors are critical for highlighting key information:
    <ul>
      <li><em>Red:</em> Signals critical attention.</li>
      <li><em>Yellow:</em> Indicates warnings.</li>
      <li><em>Green:</em> Denotes positive actions.</li>
    </ul>
  </div>
<div>
   <div class="d-bgc-secondary d-bar-400"><svg-loader class="d-fl1" name="color-foreground-icon" /></div>
    <h4>Icons</h4>
    Icon colors profoundly impact readability, user interaction, communicating meaning and status at a glance.
  </div>
</div>
<h3 id="surfaces" tabindex="-1" class="d-docsite--header-3">Surfaces <a class="header-anchor d-link d-docsite--link d-link" href="#surfaces" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Surface colors define the background of UI elements, from pages and modals to tables and cards. Neutrals provide the primary backdrop, while status colors add emphasis and context.</p>
<div class="d-d-grid d-g-300 d-g-cols2">
  <div>
    <div class="d-bgc-secondary d-bar-400"><svg-loader class="d-fl1" name="color-surface-neutral" /></div>
  </div>
  <div>
     <div class="d-bgc-secondary d-bar-400"><svg-loader class="d-fl1" name="color-surface-status" /></div>
  </div>
</div>
<h3 id="borders" tabindex="-1" class="d-docsite--header-3">Borders <a class="header-anchor d-link d-docsite--link d-link" href="#borders" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Borders delineate content areas and components, using neutrals for subtle definition and status colors for heightened emphasis. Inverted colors ensure clarity on darker surfaces.</p>
<div class="d-d-grid d-g-300 d-g-cols2">
  <div>
    <div class="d-bgc-secondary d-bar-400"><svg-loader class="d-fl1" name="color-border-ai" /></div>
  </div>
  <div>
     <div class="d-bgc-secondary d-bar-400"><svg-loader class="d-fl1" name="color-border-status" /></div>
  </div>
</div>
<h3 id="charts" tabindex="-1" class="d-docsite--header-3">Charts <a class="header-anchor d-link d-docsite--link d-link" href="#charts" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">View our <RouteLink to="/foundations/colors/chart-colors/" class="d-docsite--link d-link">Chart Colors</RouteLink> documentation for more information.</p>
<div class="d-d-grid d-g-300 d-g-cols2">
  <div class="d-bgc-secondary d-bar-400">
   <svg-loader name="chart-singlecolor" />
  </div>
  <div class="d-bgc-secondary d-bar-400">
   <svg-loader name="chart-semantic" />
  </div>
  <div class="d-bgc-secondary d-bar-400">
   <svg-loader name="chart-categorical" />
  </div>
  <div class="d-bgc-secondary d-bar-400">
   <svg-loader name="chart-sequential" />
  </div>
</div>
<h2 id="best-practices" tabindex="-1" class="d-docsite--header-2">Best Practices <a class="header-anchor d-link d-docsite--link d-link" href="#best-practices" aria-hidden="true">#</a></h2>
<h3 id="preserve-color-semantics" tabindex="-1" class="d-docsite--header-3">Preserve Color Semantics <a class="header-anchor d-link d-docsite--link d-link" href="#preserve-color-semantics" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use feedback colors consistently to maintain clear communication and avoid confusion.</p>
<dialtone-usage class="d-d-grid d-g-300 d-g-cols2">
  <template #do>
    <div class="d-bgc-secondary d-bar-400">
      <svg-loader class="d-fl1" name="color-semantics-do" />
    </div>
  </template>
  <template #dont>
    <div class="d-bgc-secondary d-bar-400">
      <svg-loader class="d-fl1" name="color-semantics-dont" />
    </div>
  </template>
</dialtone-usage>
<h3 id="semantics-over-base-values" tabindex="-1" class="d-docsite--header-3">Semantics Over Base Values <a class="header-anchor d-link d-docsite--link d-link" href="#semantics-over-base-values" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Embrace semantic tokens to separate color values from their contextual meaning, enhancing maintainability and clarity.</p>
<dialtone-usage class="d-d-grid d-g-300 d-g-cols2">
  <template #do>
    <div class="d-bgc-secondary d-bar-400">
      <svg-loader class="d-fl1" name="color-token-do" />
    </div>
  </template>
  <template #dont>
    <div class="d-bgc-secondary d-bar-400">
      <svg-loader class="d-fl1" name="color-token-dont" />
    </div>
  </template>
</dialtone-usage>
<h3 id="prioritize-contrast" tabindex="-1" class="d-docsite--header-3">Prioritize Contrast <a class="header-anchor d-link d-docsite--link d-link" href="#prioritize-contrast" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">We primarily employ the APCA for precise contrast evaluation, ensuring readability for all users.</p>
<p class="d-docsite--paragraph"><strong>Refer to Accessibility Guidelines for detailed information.</strong></p>
<dialtone-usage class="d-d-grid d-g-300 d-g-cols2">
  <template #do>
    <div class="d-bgc-secondary d-bar-400">
      <svg-loader class="d-fl1" name="color-contrast-do" />
    </div>
  </template>
  <template #dont>
    <div class="d-bgc-secondary d-bar-400">
      <svg-loader class="d-fl1" name="color-contrast-dont" />
    </div>
  </template>
</dialtone-usage>
<h3 id="uniformity" tabindex="-1" class="d-docsite--header-3">Uniformity <a class="header-anchor d-link d-docsite--link d-link" href="#uniformity" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Maintain color consistency across similar components to build intuitive user patterns.</p>
<dialtone-usage class="d-d-grid d-g-300 d-g-cols2">
  <template #do>
    <div class="d-bgc-secondary d-bar-400">
      <svg-loader class="d-fl1" name="color-uniform-do" />
    </div>
  </template>
  <template #dont>
    <div class="d-bgc-secondary d-bar-400">
      <svg-loader class="d-fl1" name="color-uniform-dont" />
    </div>
  </template>
</dialtone-usage>
<h2 id="modes" tabindex="-1" class="d-docsite--header-2">Modes <a class="header-anchor d-link d-docsite--link d-link" href="#modes" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Dialtone is designed to seamlessly support both light and dark themes. We supply colors through semantic tokens that automatically adapt their underlying values depending on the active mode, ensuring consistent meaning and optimal contrast in any environment.</p>
<div class="d-bgc-secondary d-bgo10 ">
 <svg-loader class="d-fl1" name="color-mode" />
</div>
<h2 id="related" tabindex="-1" class="d-docsite--header-2">Related <a class="header-anchor d-link d-docsite--link d-link" href="#related" aria-hidden="true">#</a></h2>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">If you need full list of our color palette, <RouteLink to="/foundations/colors/palette/" class="d-docsite--link d-link">Check out our list of colors</RouteLink>.</li>
<li class="d-docsite--list-element"><RouteLink to="/tokens/" class="d-docsite--link d-link">See our entire token catalog with full descriptions and values of all tokens.</RouteLink>.</li>
</ul>
</div></template>


